import { z } from "zod";

// The controlled vocabularies of the generator (DG) sizing module (EOS-03b),
// copied verbatim from the backend so a value the API can send is never
// rejected here. Sources: backend/app/domain/electrical/sources/generator_results.py
// (GeneratorSizingStatus, GeneratorSizingWarningCode, NOT_ESTABLISHED_WARNING_CODES)
// and .../sources/generator_models.py (GeneratorDutyClass, GeneratorRedundancyMode).
// generatorSizingContract.test.ts pins each list.
//
// The operating scenario is the same enum the load module uses, so it is reused
// from loadDemandContract rather than written out a second time.

export const generatorSizingStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const generatorSizingWarningCodeSchema = z.enum([
  "DERATING_APPLIED",
  "TRANSIENT_REQUIREMENT_GOVERNS",
  "TRANSIENT_PERFORMANCE_NOT_EVALUATED",
  "NO_STANDARD_RATING_AVAILABLE",
  "GROWTH_FACTOR_NOT_ESTABLISHED",
  "DESIGN_MARGIN_NOT_ESTABLISHED",
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
  "STEP_LOAD_NOT_ESTABLISHED",
  "TRANSIENT_ALLOWANCE_NOT_ESTABLISHED",
]);

export const generatorDutyClassSchema = z.enum(["STANDBY", "PRIME", "CONTINUOUS"]);

export const generatorRedundancyModeSchema = z.enum([
  "NONE",
  "N_PLUS_1",
  "TWO_N",
]);

/** The six warnings that make a result REVIEW_REQUIRED rather than WARNING. */
export const NOT_ESTABLISHED_WARNING_CODES = [
  "GROWTH_FACTOR_NOT_ESTABLISHED",
  "DESIGN_MARGIN_NOT_ESTABLISHED",
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
  "STEP_LOAD_NOT_ESTABLISHED",
  "TRANSIENT_ALLOWANCE_NOT_ESTABLISHED",
] as const;

/**
 * Carried by every DG result (A17, GAP-017): voltage and frequency dip are not
 * evaluated. It gives WARNING, never REVIEW_REQUIRED.
 */
export const TRANSIENT_PERFORMANCE_NOT_EVALUATED = "TRANSIENT_PERFORMANCE_NOT_EVALUATED";

/** The choices the engineer must make; the backend imposes no default (A17). */
export const REQUIRED_GENERATOR_CHOICES = ["duty_class", "redundancy_mode", "scenario"] as const;

export type GeneratorSizingStatus = z.infer<typeof generatorSizingStatusSchema>;
export type GeneratorSizingWarningCode = z.infer<typeof generatorSizingWarningCodeSchema>;
export type GeneratorDutyClass = z.infer<typeof generatorDutyClassSchema>;
export type GeneratorRedundancyMode = z.infer<typeof generatorRedundancyModeSchema>;
